import { useCallback } from "react";
import {
  AmpFileType,
  useAmpLibraryCreateFileMutation,
  useAmpLibraryUploadMutation,
} from "src/graphql";
import { uploadName } from "src/pages/projects/library/canvas/canvas-uploads";

/** Puts a file in storage and returns what `createAmpFile` needs to file it. */
export function useUploadFile() {
  const { mutateAsync } = useAmpLibraryUploadMutation();
  return useCallback(
    async (file: File, signal?: AbortSignal) => {
      const filetype = file.type || "application/octet-stream";
      const { initiateAmpFileUpload } = await mutateAsync({
        filename: file.name,
        fileSizeBytes: file.size,
      });
      const response = await fetch(initiateAmpFileUpload.uploadUrl, {
        method: "PUT",
        signal,
        body: file,
        headers: { "Content-Type": filetype },
      });
      if (!response.ok) throw new Error("Upload failed. Try again.");
      return {
        filename: file.name,
        filetype,
        s3Key: initiateAmpFileUpload.s3Key,
      };
    },
    [mutateAsync],
  );
}

/** Puts a file at the top level of the Library, outside Drafts, and returns its file id. */
export function useUploadToLibrary() {
  const upload = useUploadFile();
  const { mutateAsync: createFile } = useAmpLibraryCreateFileMutation();
  return async (file: File) => {
    const { createAmpFile } = await createFile({
      input: {
        type: AmpFileType.Upload,
        name: uploadName(file.name),
        folderId: null,
        ...(await upload(file)),
      },
    });
    return createAmpFile.id;
  };
}
